import { Layout, Badge, Dropdown, Avatar, Space, Button, Breadcrumb } from "antd";
import {
  BellOutlined,
  GlobalOutlined,
  CalendarOutlined,
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { get_notification } from "utils/api";
import apiClient from "@api/apiClient";

const { Header: AntHeader } = Layout;

// ==============================|| HEADER ||============================== //

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  // State for user details and notifications
  const [userDetails, setUserDetails] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);

  // Load user details from localStorage on mount
  useEffect(() => {
    const storedUserDetails = localStorage.getItem("user_details");
    if (storedUserDetails) {
      try {
        setUserDetails(JSON.parse(storedUserDetails));
      } catch (error) {
        console.error("Error parsing user details:", error);
      }
    }
  }, []);

  // Fetch notification count
  useEffect(() => {
    const fetchNotificationCount = () => {
      get_notification(
        (res) => {
          if (res?.status === "success" && res?.data) {
            setNotificationCount(res.data.length);
          }
        },
        { count: "count" }
      );
    };

    fetchNotificationCount();
    // Refresh notification count every 30 seconds
    const interval = setInterval(fetchNotificationCount, 30000);

    return () => clearInterval(interval);
  }, []);

  // Get display name from user details
  const getDisplayName = () => {
    if (!userDetails) return "User";

    const firstName = userDetails.first_name || "";
    const lastName = userDetails.last_name || "";

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (userDetails.username) {
      return userDetails.username;
    }

    return "User";
  };

  // Generate breadcrumb items from current path
  const getBreadcrumbItems = () => {
    const pathSnippets = location.pathname.split("/").filter((i) => i);

    const items = [
      {
        title: (
          <Link to="/">
            <HomeOutlined />
          </Link>
        ),
      },
    ];

    pathSnippets.forEach((snippet, index) => {
      const url = `/${pathSnippets.slice(0, index + 1).join("/")}`;
      const title = snippet
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      items.push({
        title: index === pathSnippets.length - 1 ? title : <Link to={url}>{title}</Link>,
      });
    });

    return items;
  };

  // User dropdown menu items
  const userMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: "Profile",
      onClick: () => navigate("/user-profile"),
    },
    // {
    //   key: "settings",
    //   icon: <SettingOutlined />,
    //   label: "Settings",
    //   onClick: () => navigate("/company-profile"),
    // },
    // {
    //   type: "divider",
    // },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Logout",
      onClick: async () => {
        const refreshToken = localStorage.getItem("refreshToken");
        try {
          // Call SSO logout API
          const response = await apiClient.post("/api/logout/", {
            refresh: refreshToken,
          });
          // Clear localStorage
          localStorage.clear();
          // Redirect to Auth frontend logout URL
          const authLogoutUrl = response?.data?.data?.auth_login_url;          
          if (authLogoutUrl) {
            window.location.href = authLogoutUrl;
          } else {
            navigate("/login");
          }
        } catch (error) {
          // Even if API fails, clear localStorage and redirect
          localStorage.clear();
          const authLoginUrl = import.meta.env.VITE_NEXGEN_AUTH_URL || "";
          window.location.href = `${authLoginUrl}/login`;
        }
      },
    },
  ];

  // Handle language change
  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("lang", lang);
  };

  // Language dropdown menu items
  const languageMenuItems = [
    {
      key: "en",
      label: "English",
      onClick: () => handleLanguageChange("en")
    },
    // {
    //   key: "hi",
    //   label: "Hindi",
    //   onClick: () => handleLanguageChange("hi")
    // },
    // {
    //   key: "mr",
    //   label: "Marathi",
    //   onClick: () => handleLanguageChange("mr")
    // },
    // {
    //   key: "kn",
    //   label: "Kannada",
    //   onClick: () => handleLanguageChange("kn")
    // },
    {
      key: "vi",
      label: "Vietnamese",
      onClick: () => handleLanguageChange("vi")
    },
  ];

  return (
    <AntHeader className="bg-white px-4 flex items-center justify-between sticky top-0 shadow-sm border-b border-gray-100 h-16" style={{ zIndex: 1000 }}>
      {/* Left Section - Breadcrumbs */}
      <div className="flex items-center">
        <Breadcrumb
          items={getBreadcrumbItems()}
          className="hidden md:flex"
        />
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-2">
        {/* Language Selector */}
        <Dropdown
          menu={{ items: languageMenuItems }}
          trigger={["click"]}
          placement="bottomRight"
          overlayStyle={{ zIndex: 1050 }}
        >
          <Button type="text" icon={<GlobalOutlined />} className="hover:bg-gray-100" />
        </Dropdown>

        {/* Calendar */}
        <Button
          type="text"
          icon={<CalendarOutlined />}
          className="hover:bg-gray-100"
          onClick={() => navigate("/calendar")}
        />

        {/* Notifications */}
        <Badge count={notificationCount} size="small">
          <Button
            type="text"
            icon={<BellOutlined />}
            className="hover:bg-gray-100"
            onClick={() => navigate("/notifications")}
          />
        </Badge>

        {/* User Profile */}
        <Dropdown
          menu={{ items: userMenuItems }}
          trigger={["click"]}
          placement="bottomRight"
          overlayStyle={{ zIndex: 1050 }}
        >
          <Space
            className="cursor-pointer hover:bg-gray-100 rounded-lg ml-2 h-10 p-3"
          >
            <Avatar size={24} icon={<UserOutlined />} className="bg-primary-600" />
            <span className="hidden md:inline text-sm font-medium">{getDisplayName()}</span>
          </Space>
        </Dropdown>
      </div>
    </AntHeader>
  );
};


export default Header;
